// ── Typing a clause the way the contract numbers it ────────────────────────
//
// The middleware numbers the paragraphs of a special condition, and of a
// clause added to the unregistered plan or lease block, the way the precedent
// numbers its own: 14.1 → (a) → (i), or C.7.1 → (a) → (i)
// (`lib/condition-numbering.js` there). This is the box staff type them in,
// and it shows that numbering as they type:
//
// - every line is a paragraph, and the number it will print under is written
//   in front of it;
// - **Tab** takes a line down a level, **Shift+Tab** brings it back;
// - **Enter** starts the next paragraph at the same level, and on an empty
//   lettered one brings it back a level instead, the way a word processor
//   ends a list;
// - **Backspace** at the start of a paragraph's wording brings it back a
//   level, and at the top level joins it to the one above.
//
// **The text is the record.** What is typed here is what is saved, numbers
// and all — "14.1 The purchaser must:" with "    (a) pay" under it, four
// spaces a level — and the middleware reads the level from the indent and
// renumbers whatever it is sent. So a number that went stale (a clause moved
// up the list) is relabelled here on sight and harmless if it is not: the
// printed numbering is never this file's to decide.
import { useLayoutEffect, useRef } from "react";

const MAX_DEPTH = 3;
const STEP = "    ";
const ROMANS = ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x",
  "xi", "xii", "xiii", "xiv", "xv", "xvi", "xvii", "xviii", "xix", "xx"];

const letter = (n) => String.fromCharCode(97 + ((n - 1) % 26)).repeat(Math.floor((n - 1) / 26) + 1);
const roman = (n) => ROMANS[n - 1] || String(n);

// What a line opens with, read the way the middleware reads it. A marker on
// its own ("14.2", "(a)") is still a marker: it is a paragraph whose wording
// has not been typed yet.
export const readMarker = (line) => {
  let m = line.match(/^[-*•–·](?:\s+|$)(.*)$/);
  if (m) return { kind: "bullet", body: m[1] };
  m = line.match(/^([A-Z]\.)?(\d{1,2}(?:\.\d{1,2})*)([.)])?(?:\s+|$)(.*)$/);
  if (m && (m[1] || m[3] || m[2].includes("."))) return { kind: "number", body: m[4] };
  m = line.match(/^\(?([ivx]{1,5})\)(?:\s+|$)(.*)$/);
  if (m && ROMANS.includes(m[1])) return { kind: "roman", value: m[1], body: m[2] };
  m = line.match(/^\(?([a-z]{1,2})\)(?:\s+|$)(.*)$/);
  if (m) return { kind: "letter", value: m[1], body: m[2] };
  return { kind: "none", body: line };
};

// Lines into `{ depth, body }`, or `{ blank }`, or `{ more, depth }` for an
// indented line with no marker — the rest of the paragraph above it.
export const parseLines = (text) => {
  const out = [];
  let depth = 0;
  let lastLetter = "";
  for (const raw of String(text ?? "").split("\n")) {
    if (raw.trim() === "") { out.push({ blank: true }); continue; }
    const lead = raw.match(/^[ \t]*/)[0].replace(/\t/g, STEP).length;
    const steps = Math.floor(lead / STEP.length);
    const marker = readMarker(raw.replace(/^[ \t]+/, ""));

    if (marker.kind === "none" && steps > 0 && depth > 0) {
      out.push({ more: true, depth, body: marker.body });
      continue;
    }
    let d;
    if (steps > 0 && marker.kind !== "none") d = Math.min(MAX_DEPTH, steps + 1);
    else if (marker.kind === "number" || marker.kind === "none") d = 1;
    else if (marker.kind === "letter" || marker.kind === "bullet") d = 2;
    else {
      const continues = marker.value.length === 1 && depth === 2 && lastLetter
        && lastLetter.charCodeAt(0) + 1 === marker.value.charCodeAt(0);
      d = continues ? 2 : depth >= 2 ? 3 : 2;
    }
    if (d === 2 && (marker.kind === "letter" || marker.kind === "roman")) lastLetter = marker.value;
    depth = Math.min(d, depth + 1);
    out.push({ depth, body: marker.body });
  }
  return out;
};

// Back into text, numbered. `cite` is the clause's own number ("14", "C.7");
// `always` numbers a single top-level paragraph too (a special condition, as
// the precedent's 13.1), where a clause added to a block leaves it bare.
export const renderLines = (lines, { cite, always = true } = {}) => {
  const tops = lines.filter(l => !l.blank && !l.more && l.depth === 1).length;
  const counts = [0, 0, 0, 0];
  return lines.map(l => {
    if (l.blank) return "";
    if (l.more) return STEP.repeat(l.depth) + l.body;
    counts[l.depth] += 1;
    for (let d = l.depth + 1; d <= MAX_DEPTH; d += 1) counts[d] = 0;
    const n = counts[l.depth];
    const label = l.depth === 1 ? (always || tops > 1 ? `${cite}.${n}` : "")
      : l.depth === 2 ? `(${letter(n)})` : `(${roman(n)})`;
    return STEP.repeat(l.depth - 1) + (label ? `${label} ` : "") + l.body;
  }).join("\n");
};

export const relabel = (text, options) => renderLines(parseLines(text), options);

// Where the wording of line `i` starts, in the rendered text.
const bodyStart = (rendered, i) => {
  const line = rendered.split("\n")[i] ?? "";
  const lead = line.match(/^ */)[0].length;
  const rest = line.slice(lead);
  const marker = readMarker(rest);
  return lead + (rest.length - marker.body.length);
};

const lineAt = (text, pos) => text.slice(0, pos).split("\n").length - 1;
const offsetOfLine = (text, i) => text.split("\n").slice(0, i).reduce((n, l) => n + l.length + 1, 0);

// One key, applied to the text: `{ text, caret }` for the box to show, or null
// where the key is the browser's to handle.
export const applyKey = (text, { key, shiftKey, start, end }, options) => {
  const lines = parseLines(text);
  const rendered = renderLines(lines, options);
  const first = lineAt(text, start);
  const last = lineAt(text, end);
  const here = lines[first];

  if (key === "Tab") {
    for (let i = first; i <= last; i += 1) {
      const l = lines[i];
      if (!l || l.blank || l.more) continue;
      const above = lines.slice(0, i).reverse().find(x => !x.blank && !x.more);
      const max = Math.min(MAX_DEPTH, (above?.depth || 0) + 1);
      l.depth = shiftKey ? Math.max(1, l.depth - 1) : Math.min(max, l.depth + 1);
    }
    const next = renderLines(lines, options);
    return { text: next, caret: offsetOfLine(next, first) + bodyStart(next, first) };
  }

  if (start !== end || !here || here.blank) return null;
  const lineStart = offsetOfLine(rendered, first);
  const at = start - lineStart - bodyStart(rendered, first);
  if (at < 0) return null;

  if (key === "Enter" && !shiftKey) {
    // An empty lettered paragraph: come back a level, as a list ends.
    if (!here.more && here.body.trim() === "" && here.depth > 1) {
      here.depth -= 1;
      const next = renderLines(lines, options);
      return { text: next, caret: offsetOfLine(next, first) + bodyStart(next, first) };
    }
    const tail = here.body.slice(at);
    here.body = here.body.slice(0, at);
    lines.splice(first + 1, 0, here.more ? { more: true, depth: here.depth, body: tail } : { depth: here.depth, body: tail });
    const next = renderLines(lines, options);
    return { text: next, caret: offsetOfLine(next, first + 1) + bodyStart(next, first + 1) };
  }

  if (key === "Backspace" && at === 0) {
    if (!here.more && here.depth > 1) {
      here.depth -= 1;
      const next = renderLines(lines, options);
      return { text: next, caret: offsetOfLine(next, first) + bodyStart(next, first) };
    }
    if (first === 0) return { text: rendered, caret: lineStart + bodyStart(rendered, 0) };
    const above = lines[first - 1];
    if (above.blank) {
      lines.splice(first - 1, 1);
      const next = renderLines(lines, options);
      return { text: next, caret: offsetOfLine(next, first - 1) + bodyStart(next, first - 1) };
    }
    const join = above.body.length;
    above.body += here.body;
    lines.splice(first, 1);
    const next = renderLines(lines, options);
    return { text: next, caret: offsetOfLine(next, first - 1) + bodyStart(next, first - 1) + join };
  }
  return null;
};

// The box. `cite` and `always` as `renderLines`; `onChange` gets the text,
// numbered.
export const NumberedClauseInput = ({ value, onChange, cite, always = true, style, ...rest }) => {
  const ref = useRef(null);
  const caret = useRef(null);
  const options = { cite, always };
  const shown = relabel(value, options);

  useLayoutEffect(() => {
    if (caret.current === null || !ref.current) return;
    ref.current.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  const onKeyDown = (e) => {
    if (!["Tab", "Enter", "Backspace"].includes(e.key) || e.altKey || e.ctrlKey || e.metaKey) return;
    const { selectionStart: start, selectionEnd: end } = e.target;
    const out = applyKey(shown, { key: e.key, shiftKey: e.shiftKey, start, end }, options);
    if (!out) return;
    e.preventDefault();
    caret.current = out.caret;
    onChange(out.text);
  };

  // Typed or pasted text is relabelled as it lands, keeping the caret the
  // same distance from the end of its line — the numbers change at the start
  // of a line, never under the caret.
  const onInput = (e) => {
    const raw = e.target.value;
    const pos = e.target.selectionStart;
    const i = lineAt(raw, pos);
    const fromEnd = (raw.split("\n")[i] ?? "").length - (pos - offsetOfLine(raw, i));
    const next = relabel(raw, options);
    const line = next.split("\n")[i] ?? "";
    caret.current = offsetOfLine(next, i) + Math.max(bodyStart(next, i), line.length - fromEnd);
    onChange(next);
  };

  return (
    <textarea
      ref={ref}
      value={shown}
      onChange={onInput}
      onKeyDown={onKeyDown}
      spellCheck
      style={{ whiteSpace: "pre-wrap", tabSize: 4, ...style }}
      {...rest}
    />
  );
};
